import type { ChangeEvent, FC, MouseEvent } from "react";
import { useSelector } from "react-redux";
import { Button, Margin } from "@kivra/react-components";
import { ButtonContainer } from "../layouts/ButtonContainer";
import { useCopy } from "../../utils/globalContext";
import type { RootState } from "../../store/rootReducer";
import {
  getIsSignupDisabled,
  getIsUserLoading,
  getIsUsernameInvalid,
  getPhoneNumber,
  getTunnistusData,
  getUsername as getEmailAddress,
  setPhoneNumber,
  setUsername,
  signupUser,
} from "../../store/auth";
import type { TunnistusState } from "../../store/auth";
import { useAppDispatch } from "../../store/configureStore";
import { KivraHeaderRouter } from "../header/KivraHeaderRouter";
import { PresetFormValue } from "./PresetFormValue";
import { TextFieldWithOnBlurValidation } from "./TextFieldWithOnBlurValidation";

export const SignupForm: FC = () => {
  const {
    phoneNumber,
    emailAddress,
    tunnistusData,
    isLoading,
    isEmailAddressInvalid,
    isSignupDisabled,
  } = useSelector(getSignupFormState);
  const { updateEmailAddress, updatePhoneNumber, doSignup } =
    useSignupFormActions();
  const copy = useCopy();
  return (
    <div onFocus={(e) => e.target.scrollIntoView({ block: "nearest" })}>
      <KivraHeaderRouter />
      <PresetFormValue
        label={copy("input_placeholder_finland_ssn")}
        value={tunnistusData.ssn}
      />
      <Margin bottom={20} />
      <PresetFormValue
        label={copy("input_placeholder__name")}
        value={tunnistusData.givenName + " " + tunnistusData.lastName}
      />
      <Margin bottom={20} />
      <TextFieldWithOnBlurValidation
        dataTestId="phone-input"
        label={copy("input_placeholder__phone_number")}
        type="tel"
        name="phone"
        value={phoneNumber}
        onChange={updatePhoneNumber}
        autoFocus
        isInvalid={false}
        hintText={copy("input_hint__mobile_phone__register")}
      />
      <Margin bottom={20} />
      <TextFieldWithOnBlurValidation
        dataTestId="email-input"
        label={copy("input_placeholder__email")}
        type="email"
        name="email"
        autoComplete="username"
        value={emailAddress}
        onChange={updateEmailAddress}
        isInvalid={isEmailAddressInvalid}
        hintText={copy("input_hint__email__register")}
      />
      <Margin bottom={20} />
      <ButtonContainer>
        <Button
          progress={isLoading}
          disabled={isSignupDisabled}
          onClick={(event: MouseEvent) => {
            event.preventDefault();
            doSignup();
          }}
          type="submit"
          size="medium"
        >
          {copy("btn_register")}
        </Button>
      </ButtonContainer>
      <Margin bottom={56} />
    </div>
  );
};

interface SignupFormState {
  phoneNumber: string;
  emailAddress: string;
  tunnistusData: TunnistusState;
  isLoading: boolean;
  isEmailAddressInvalid: boolean;
  isSignupDisabled: boolean;
}

const getSignupFormState = (state: RootState): SignupFormState => ({
  isLoading: getIsUserLoading(state),
  emailAddress: getEmailAddress(state),
  phoneNumber: getPhoneNumber(state),
  isEmailAddressInvalid: getIsUsernameInvalid(state),
  isSignupDisabled: getIsSignupDisabled(state),
  tunnistusData: getTunnistusData(state),
});

interface SignupFormActions {
  updatePhoneNumber: (event: ChangeEvent<HTMLInputElement>) => void;
  updateEmailAddress: (event: ChangeEvent<HTMLInputElement>) => void;
  doSignup: () => void;
}

const useSignupFormActions = (): SignupFormActions => {
  const dispatch = useAppDispatch();
  return {
    updatePhoneNumber: (event: ChangeEvent<HTMLInputElement>) => {
      dispatch(setPhoneNumber(event.target.value));
    },
    updateEmailAddress: (event: ChangeEvent<HTMLInputElement>) => {
      dispatch(setUsername(event.target.value));
    },
    doSignup: () => dispatch(signupUser()),
  };
};
